import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Spinner } from 'reactstrap';
import { uri } from 'constants/common';
import InterviewInviteTable from './InterviewInviteTable';
import CancelModal from 'components/appointment/CancelModal';
import { useDispatch, useSelector } from "react-redux";
import { setActionAssign } from "slices/actionButtonSlice";
import Api from 'services/api';
import { useSearchParams } from 'react-router-dom';

const AppointmentConfirmation = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const dispatch = useDispatch();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [actionResult, setActionResult] = useState('');
  const [cancelModal, setCancelModal] = React.useState(false);

  const loadAppointment = async () => {
    try {
      setLoading(true);
      const response = await Api.Confirmation(token);
      setAppointment(response.data.data);
      setErrorMessage('');
      setActionResult('');
    } catch (error) {
      const message = error.response?.data?.message || 'Page Expired';
      setErrorMessage(message);
      setAppointment(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setErrorMessage('Invalid confirmation link.');
      setLoading(false);
      return;
    }
    loadAppointment();
  }, [token]);

  const handleConfirm = async () => {
    if (!token) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await Api.confirmConfirmation(token);
      toast.success(response.data.message || 'Appointment confirmed successfully.', {
        className: 'successToast',
      });
      setActionResult('confirmed');
      await loadAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to confirm appointment.', {
        className: 'errorToast',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const timezoneName =
    appointment?.appointmentDetails?.timezone?.name || appointment?.accounts?.timezone?.name || 'UTC';

  const formattedDate = appointment?.datetime
    ? moment(appointment.datetime, 'YYYY-MM-DD HH:mm:ss').format('MMMM D, YYYY h:mm A')
    : 'N/A';

  const interpreterName =
    appointment?.invite_details?.interpreter?.first_name ||
    appointment?.interpreter?.first_name ||
    appointment?.invite_details?.interpreter?.name ||
    appointment?.interpreter?.name ||
    'Interpreter';

  const confirmDisabled =
    appointment?.confirmation_status === 'confirmed' ||
    appointment?.confirmation_status === 'cancelled' ||
    actionResult === 'confirmed' ||
    actionResult === 'cancelled';

  if (loading) {
    return (
      <div className="loader_wrapper">
        <Spinner size="xl">Loading...</Spinner>
      </div>
    );
  }

  if (errorMessage || !appointment) {
    return (
      <div className="error-page-container">
        <img src="/images/logo.png" alt="MayaBridge" />
        <h4>OOps! {errorMessage || 'The page you were looking for doesn\'t exist.'}</h4>
        <Link to="/" className="main_button mt-5">
          Go to home page
        </Link>
      </div>
    );
  }

  const {id} = appointment.id || {};
  const handleCancel = () => {
    setCancelModal(true);
  };

  const handleCloseCancel = () => {
    setCancelModal(false);
  };

  const handleSubmit = async (data, end_point, callBack) => {
    setLoading(true);

    const response =  await Api.cancelConfirmation(token, data );
    if (response?.status >= 200 && response?.status < 300) {
      handleCloseCancel();
      toast.success(response.data.message || 'Notifications updated successfully.', {
        className: "successToast",
      });
      setLoading(false);
      await loadAppointment();
    }else{
      toast.error(response.data.message || 'Failed to update notifications.', {
          className: "errorToast",
        });
        setLoading(false);
    }
  };

  return (
    <div className="main-freePage">
      <img src="/images/logo.png" alt="MayaBridge" />
      <div className="table_wrapper">
        <div className="appointment-container">
          <div className="appointment-header">
            <p>
              APPT <b className="font-semibold"># {appointment?.appid || appointment?.id}</b>
            </p>
          </div>
          
          <InterviewInviteTable InterviewInvite={appointment} />

          {appointment?.confirmation_status === 'confirmed' || actionResult === 'confirmed' ? (
            <div className="str_row">
              <p style={{ color: 'green', fontWeight: 500 }}>
                This appointment has been confirmed successfully.
              </p>
            </div>
          ) : appointment?.confirmation_status === 'cancelled' || actionResult === 'cancelled' ? (
            <div className="str_row">
              <p style={{ color: 'red', fontWeight: 500 }}>
                This appointment has been cancelled.
              </p>
            </div>
          ) : (
            <div className="str_row">
              <div className="agreement-section">
                                    </div>
              <div className="button-group">
                <button
                  className="main_button seagreen"
                  onClick={handleConfirm}
                  disabled={confirmDisabled || submitting}
                >
                  {submitting ? 'Please wait...' : 'Confirm'}
                </button>
                <button
                  className="table_delete"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCancelModal && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content full_modal">
              <div className="modal-header header_modal align-items-center">
                <h5 className="modal-title_u">Decline Appointment</h5>
                <button type="button" className="close" onClick={() => setShowCancelModal(false)}>
                  &times;
                </button>
              </div>
              <div className="modal-body modal_form_body">
                <p>Please share a reason for declining this appointment.</p>
                <textarea
                  className="form-control"
                  rows={4}
                  value={cancelReason}
                  onChange={(event) => setCancelReason(event.target.value)}
                  placeholder="Enter cancellation reason"
                />
              </div>
              <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                <button type="button" className="main_button" onClick={handleCancel} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Decline'}
                </button>
                <button type="button" className="table_delete" onClick={() => setShowCancelModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </div>
      )}
      <CancelModal
        isOpen={cancelModal}
        onClose={handleCloseCancel}
        onConfirm={handleSubmit}
        appointmentId={id}
        item={appointment}
        loader={loading}
        end_point="interpreter-cancel"
      />
    </div>
  );
};

export default AppointmentConfirmation;
